// Kharie's Terms / Privacy Markdown reader (#316), shared so the admin editor's
// preview (BL-25) reads a document exactly as the client app will. Blocks, never
// HTML: no stored text can carry markup into either app.

/** A run of text, bold or not. */
export interface LegalInline {
  text: string;
  strong: boolean;
}

export type LegalBlock =
  | { type: "heading"; level: 2 | 3; inline: LegalInline[] }
  | { type: "paragraph"; inline: LegalInline[] }
  | { type: "list"; ordered: boolean; items: LegalInline[][] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^[-*+]\s+(.*)$/;
const NUMBERED = /^\d+[.)]\s+(.*)$/;

/** `**bold**` runs; an unpaired `**` is printed as typed. */
export function parseInline(text: string): LegalInline[] {
  const out: LegalInline[] = [];
  let rest = text;
  while (rest.length > 0) {
    const open = rest.indexOf("**");
    const close = open === -1 ? -1 : rest.indexOf("**", open + 2);
    if (open === -1 || close === -1 || close === open + 2) {
      out.push({ text: rest, strong: false });
      break;
    }
    if (open > 0) out.push({ text: rest.slice(0, open), strong: false });
    out.push({ text: rest.slice(open + 2, close), strong: true });
    rest = rest.slice(close + 2);
  }
  return out;
}

export function parseLegalMarkdown(body: string): LegalBlock[] {
  const blocks: LegalBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: "paragraph", inline: parseInline(paragraph.join(" ")) });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list !== null) {
      blocks.push({ type: "list", ordered: list.ordered, items: list.items.map(parseInline) });
      list = null;
    }
  };

  for (const raw of body.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (line === "") {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      // The screen's own title is the h1, so a document's top level is h2.
      const level = (heading[1] ?? "").length <= 2 ? 2 : 3;
      blocks.push({ type: "heading", level, inline: parseInline((heading[2] ?? "").trim()) });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const item = bullet ?? numbered;
    if (item) {
      flushParagraph();
      const ordered = numbered !== null;
      if (list !== null && list.ordered !== ordered) flushList();
      if (list === null) list = { ordered, items: [] };
      list.items.push((item[1] ?? "").trim());
      continue;
    }
    // A wrapped line under a list item continues that item, as Markdown does.
    if (list !== null) {
      const items: string[] = list.items;
      items[items.length - 1] = `${items[items.length - 1]} ${line}`;
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}
